import { useEffect, useState } from 'react';
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle, faTrash, faSave, faCancel, faBedPulse } from '@fortawesome/free-solid-svg-icons';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import { Field, Form, Formik, FieldArray, FormikErrors } from 'formik';
import { TextField } from '@mui/material';
import FormErrorContainer from '../../sections/shared/FormErrorContainer';
import * as Yup from "yup";
import useUtils from '../../../utiles/useUtiles';
import { addNetworkValidation } from '../../../utiles/formValidation'
import { CircularProgress } from '@mui/material';


interface NetworkAddFormErrors {
    csvTypeId: { label: string, value: string },
    name: string,
    status: string
}
interface NetworkAddFormValues {
    csvTypeId: { label: string, value: number },
    name: string,
    status: string
}
interface Props {
    show: boolean,
    handleClose: () => void,
    networkDetails: any
}
const ModalAddNetwork: React.FC<Props> = ({ show, handleClose, networkDetails }): JSX.Element => {

    const [loading, setLoading] = useState<boolean>(false);
    const { axiosRequest, baseUrl, toastInstance } = useUtils()
    const axios = axiosRequest();
    const toast = toastInstance();

    const csvTypeOptions = (inputValue: string): Promise<any[]> => {
        return new Promise<any[]>(async (resolve) => {
            const result: any[] = await fetchCsvTypes(inputValue);
            resolve(result);
        });
    }

    const fetchCsvTypes = (searchText: string): Promise<any[]> => {
        return new Promise<any[]>(async (resolve) => {
            const request = await axios.get(`${baseUrl()}/template_types?page=0&pageSize=5&searchText=${searchText}`);
            const { error, result, message } = request.data;

            if (error) {
                toast.error(message);
                resolve([]);
            } else {
                const data = result['rows'].map((r: any) => ({
                    label: r.name,
                    value: r.id
                }));
                resolve(data);
            }
        });
    };

    const handleSubmit = async (values: any) => {
        setLoading(true);
        var request = null;
        if (networkDetails) {
            request = await axios.put(`${baseUrl()}/template_type_networks/${networkDetails.id}`, values);
        } else {
            request = await axios.post(`${baseUrl()}/template_type_networks`, values);
        }

        const { error, message, result } = request.data;
        setLoading(false);
        if (error) {
            toast.error(message);
        } else {
            toast.success(message);
            handleClose();
        }
    };

    return (
        <Modal size="lg" show={show} onHide={handleClose} backdrop="static">
            <Formik
                initialValues={{
                    csvTypeId: networkDetails && networkDetails['csvType'] && {
                        label: networkDetails['csvType']['name'],
                        value: networkDetails['csvType']['id']
                    } || '',
                    name: networkDetails && networkDetails['name'] || '',
                    status: networkDetails && '' + networkDetails['status'] || "1"
                }}
                validateOnChange={true}
                validateOnBlur={true}
                validationSchema={addNetworkValidation}
                onSubmit={async (values, helpers) => {
                    const params = {
                        ...values,
                        csvTypeId: values['csvTypeId'] && values['csvTypeId']['value'] || null,
                        status: +values['status']
                    };
                    handleSubmit(params);
                }}>
                {({ errors, values, handleChange }: { errors: FormikErrors<NetworkAddFormErrors>, values: NetworkAddFormValues, handleChange: any }) => (
                    <Form>
                        <Modal.Header>
                            <Modal.Title>{networkDetails && 'Update' || 'Add'} Network</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <div className="row">
                                <div className="col-md-12">
                                    <div className="cardCustom p-3">
                                        <div className="row mb-2">
                                            <div className="col-md-6">
                                                <label htmlFor="name">Template Type<strong className='text-danger'>*</strong></label>
                                                <AsyncSelect
                                                    data-cy="selectTemplateType"
                                                    classNamePrefix={"selectTemplateType"}
                                                    cacheOptions
                                                    loadOptions={csvTypeOptions}
                                                    defaultOptions
                                                    name="csvTypeId"
                                                    value={values.csvTypeId}
                                                    onChange={selectedOption => {
                                                        let event = { target: { name: 'csvTypeId', value: selectedOption } };
                                                        handleChange(event);
                                                    }}
                                                />
                                                <FormErrorContainer nameError={errors.csvTypeId?.value} />
                                            </div>
                                        </div>

                                        <div className="row mb-2">
                                            <div className="col-md-6">
                                                <label htmlFor="name">Network Name<strong className='text-danger'>*</strong></label>
                                                <Field data-cy="inputName" name="name" className="form-control" placeholder="Name" autoComplete={"new-password"} />
                                                <FormErrorContainer nameError={errors.name} />
                                            </div>

                                            <div className="col-md-6">
                                                <label>Status</label>
                                                <div className='d-flex gap-3'>
                                                    <div className='form-check mr-4'>
                                                        <Field type="radio" name="status" value="1" className='form-check-input' id="status1" />
                                                        <label htmlFor='status1' className='form-check-label'>
                                                            Active
                                                        </label>
                                                    </div>
                                                    <div className='form-check'>
                                                        <Field type="radio" name="status" value="0" className='form-check-input' id="status2" />
                                                        <label htmlFor='status2' className='form-check-label'>
                                                            Inactive
                                                        </label>
                                                    </div>
                                                    <FormErrorContainer nameError={errors.status} />
                                                </div>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </Modal.Body>
                        <Modal.Footer className="justify-content-center d-flex flex-nowrap gap-3">
                            <button type="button" className="btn btn-md revrex-outline-btn mt-3" onClick={handleClose}>
                                <span>Cancel</span>
                            </button>
                            <button data-cy="btnSubmit" type="submit" className="btn btn-md revrex-primary-btn mt-3" disabled={loading}>
                                <span>{loading && (<CircularProgress size={20} color={'success'} />) || 'Save'}</span>
                            </button>
                        </Modal.Footer>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
};

export default ModalAddNetwork;